export type InterestMethod = 'NONE' | 'FLAT' | 'REDUCING_BALANCE'

export type LoanStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'DISBURSED'
  | 'CLOSED' | 'CANCELLED' | 'WRITTEN_OFF'

export type InstalmentStatus = 'DUE' | 'PARTIAL' | 'PAID' | 'WAIVED' | 'SETTLED_EARLY'

/**
 * The three interest bases, described rather than named.
 *
 * Flat and reducing at the same rate are not the same price — flat charges the whole rate on
 * the original balance for the whole term — and a screen that shows only "10%" invites the
 * two to be compared as if they were.
 */
export const INTEREST_METHOD_LABELS: Record<InterestMethod, string> = {
  NONE: 'Interest-free',
  FLAT: 'Flat — the whole rate on the original amount, for the whole term',
  REDUCING_BALANCE: 'Reducing balance — interest each month on what is still owed',
}

export const LOAN_STATUS_LABELS: Record<LoanStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Awaiting approval',
  APPROVED: 'Approved, not yet paid out',
  REJECTED: 'Rejected',
  DISBURSED: 'Being repaid',
  CLOSED: 'Repaid in full',
  CANCELLED: 'Cancelled',
  WRITTEN_OFF: 'Written off',
}

export const INSTALMENT_STATUS_LABELS: Record<InstalmentStatus, string> = {
  DUE: 'Due',
  PARTIAL: 'Part paid',
  PAID: 'Paid',
  WAIVED: 'Waived',
  SETTLED_EARLY: 'Cleared by early settlement',
}

export interface LoanTypeView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  description: string | null
  maximumAmount: number | null
  maximumTenorMonths: number | null
  minimumServiceMonths: number | null
  interestMethod: string
  interestRate: number | null
  deductionElementId: string | null
  requiresApproval: boolean
  active: boolean
}

export interface LoanView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  loanTypeId: string
  loanTypeName: string | null
  reference: string
  principal: number
  currency: string
  tenorMonths: number
  interestMethod: string
  interestRate: number | null
  requestedOn: string
  firstInstalmentOn: string | null
  purpose: string | null
  status: string
  submittedAt: string | null
  decidedAt: string | null
  decidedBy: string | null
  decisionComment: string | null
  disbursedOn: string | null
  closedOn: string | null
  outstandingPrincipal: number
}

export interface LoanInstalmentView {
  id: string
  seq: number
  dueOn: string
  principalDue: number
  interestDue: number
  totalDue: number
  principalPaid: number
  interestPaid: number
  /** What payroll could not take, carried to the next month rather than skipped. */
  arrears: number
  outstanding: number
  status: string
  settledOn: string | null
}

export interface LoanRepaymentView {
  id: string
  instalmentId: string | null
  payrollRunId: string | null
  paidOn: string
  principalAmount: number
  interestAmount: number
  source: string
  notes: string | null
}

export interface LoanDetailView {
  loan: LoanView
  schedule: LoanInstalmentView[]
  repayments: LoanRepaymentView[]
  /** Shown next to the loan's own balance so a drift between them is visible. */
  scheduleOutstanding: number
}
